/**
 * Generato automaticamente.
 * framework-cmp-domande
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type { OnereDTO } from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Recupera il dettaglio dell'onere
 * @summary dettaglio dell'onere
 */
export const getFrontOneriId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<OnereDTO>(
    { url: `/framework-cmp-domande/oneri/${id}`, method: 'get', signal },
    options,
  );
};

export const getGetFrontOneriIdQueryKey = (id: string) =>
  [`/framework-cmp-domande/oneri/${id}`] as const;

export const getGetFrontOneriIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getFrontOneriId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontOneriId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getFrontOneriId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetFrontOneriIdQueryKey(id);

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getFrontOneriId>>> = ({
    signal,
  }) => getFrontOneriId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetFrontOneriIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getFrontOneriId>>
>;
export type GetFrontOneriIdQueryError = ErrorType<unknown>;

/**
 * @summary dettaglio dell'onere
 */
export const useGetFrontOneriId = <
  TData = Awaited<ReturnType<typeof getFrontOneriId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getFrontOneriId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetFrontOneriIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Cancellazione dell'onere
 * @summary eliminazione onere
 */
export const deleteFrontOneriId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    { url: `/framework-cmp-domande/oneri/${id}`, method: 'delete' },
    options,
  );
};

export const getDeleteFrontOneriIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteFrontOneriId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteFrontOneriId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteFrontOneriId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteFrontOneriId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteFrontOneriIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteFrontOneriId>>
>;

export type DeleteFrontOneriIdMutationError = ErrorType<unknown>;

/**
 * @summary eliminazione onere
 */
export const useDeleteFrontOneriId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteFrontOneriId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getDeleteFrontOneriIdMutationOptions(options);

  return useMutation(mutationOptions);
};
